import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  domError,
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

async function storageOperation(): Promise<Record<string, string>> {
  const results: Record<string, string> = {};
  const key = `abc-capability-${crypto.randomUUID()}`;
  try {
    localStorage.setItem(key, 'ok');
    results.localStorage =
      localStorage.getItem(key) == 'ok' ? 'success' : 'error';
    localStorage.removeItem(key);
  } catch (error) {
    results.localStorage = domError(error).name;
  }
  try {
    sessionStorage.setItem(key, 'ok');
    results.sessionStorage =
      sessionStorage.getItem(key) == 'ok' ? 'success' : 'error';
    sessionStorage.removeItem(key);
  } catch (error) {
    results.sessionStorage = domError(error).name;
  }
  return results;
}

async function collect(): Promise<RawData> {
  const persisted = navigator.storage?.persisted
    ? await navigator.storage.persisted()
    : null;
  const operationResults = await storageOperation();
  return {
    cookies: navigator.cookieEnabled,
    indexedDb: 'indexedDB' in window,
    cacheStorage: 'caches' in window,
    storageManager: 'storage' in navigator,
    persisted,
    operationResults,
  };
}

function validateStorage(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  for (const key of [
    'cookies',
    'indexedDb',
    'cacheStorage',
    'storageManager',
  ]) {
    if (rawData[key] != true) {
      reasons.push(`${key} is unavailable`);
    }
  }
  const operations = rawData.operationResults;
  if (
    typeof operations != 'object' ||
    operations == null ||
    (operations as Record<string, unknown>).localStorage != 'success' ||
    (operations as Record<string, unknown>).sessionStorage != 'success'
  ) {
    reasons.push('Web Storage operation failed');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return validateStorage(rawData).valid ? 'Storage works' : 'Storage limited';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateStorage(rawData);
}

export const storageCapabilitiesTest: TestDefinition = {
  id: 'storageCapabilities',
  title: 'Storage capabilities',
  category: 'shared',
  format,
  run,
  validate,
};
